import { Button } from '@emailforge/ui';

import { Icon } from '@/components/ui/icons';
import {
  type UploadedAsset,
  useDeleteAssetMutation,
  useListAssetsQuery,
  useStorageUsageQuery,
} from '@/features/uploads/uploadsApi';

export function AssetLibraryDrawer({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  /** Called when the user clicks an asset; receives its public URL. */
  onPick: (url: string) => void;
}) {
  const { data: assets, isLoading } = useListAssetsQuery(undefined, { skip: !open });
  const { data: usage } = useStorageUsageQuery(undefined, { skip: !open });
  const [deleteAsset] = useDeleteAssetMutation();

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 50 }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 480,
          background: 'var(--paper)',
          borderLeft: '1px solid var(--line)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
          zIndex: 51,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--line-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Icon.image size={15} />
          <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>Asset library</div>
          {usage && (
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {formatBytes(usage.bytesUsed)} / {formatBytes(usage.bytesQuota)} ·{' '}
              {usage.fileCount} files
            </span>
          )}
          <Button variant="ghost" size="sm" icon onClick={onClose} aria-label="Close">
            <Icon.x size={12} />
          </Button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
          {isLoading && (
            <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              Loading…
            </div>
          )}

          {!isLoading && (assets ?? []).length === 0 && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-3)',
                textAlign: 'center',
                padding: 30,
                lineHeight: 1.5,
              }}
            >
              No uploads yet.
              <br />
              Drop an image into any image block to start.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {(assets ?? []).map((a) => (
              <AssetTile
                key={a.id}
                asset={a}
                onPick={() => {
                  onPick(a.url);
                  onClose();
                }}
                onDelete={() => {
                  if (confirm(`Delete "${a.filename}"? This cannot be undone.`)) {
                    void deleteAsset(a.id);
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function AssetTile({
  asset,
  onPick,
  onDelete,
}: {
  asset: UploadedAsset;
  onPick: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--line-soft)',
        borderRadius: 'var(--r-2)',
        background: 'var(--paper)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <button
        onClick={onPick}
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          padding: 0,
          border: 'none',
          cursor: 'pointer',
          background: 'var(--paper-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={asset.url}
          alt={asset.filename}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </button>
      <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              color: 'var(--ink-2)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={asset.filename}
          >
            {asset.filename}
          </div>
          <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)' }}>
            {formatBytes(asset.sizeBytes)}
          </div>
        </div>
        <button
          onClick={onDelete}
          aria-label="Delete"
          style={{
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--ink-3)',
            padding: 2,
          }}
        >
          <Icon.trash size={11} />
        </button>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
